/**
 * PreloadCache - LRU cache for preloaded PDF blobs
 *
 * Features:
 * - Count-based eviction (max 50 entries)
 * - Size-based eviction guard (max 100MB total)
 * - TTL tracking for signed URLs
 * - Automatic blob URL revocation on eviction
 */
/* eslint-disable camelcase */

const PRELOAD_CONFIG = {
  MAX_CONCURRENT_BLOBS: 4, // Background preloads (user clicks bypass this limit)
  // Skip blob preload if > 20 pages (checked before fetch)
  // NOTE: Keep in sync with backend MAX_PAGES_FOR_PRELOAD in app/controllers/concerns/objects_module.rb
  MAX_PAGES_FOR_BLOB: 20,
  MAX_SIZE_MB_FOR_BLOB: 5, // Discard blob after fetch if > 5MB (checked via Content-Length)
  SIGNED_URL_TTL: 120 * 1000, // 120 seconds (matches backend)
  MAX_CACHED_BLOBS: 50, // Evict oldest when count exceeded (matches max records per page)
  MAX_CACHE_SIZE_MB: 100, // Evict oldest when total size exceeded
  RETRY_DELAY_MS: 1000, // Fixed 1s delay before single retry, then mark as error
  PERMISSION_TIMEOUT_MESSAGE_MS: 3000, // Show "Taking longer..." after 3s
};

/**
 * @typedef {Object} CacheEntry
 * @property {string} fileToken
 * @property {string|null} signedUrl
 * @property {Blob|null} pdfBlob
 * @property {string|null} blobUrl - URL.createObjectURL result
 * @property {number|null} blobSizeBytes - From Content-Length
 * @property {number} fetchedAt
 * @property {number} expiresAt
 * @property {number} lastAccessedAt
 * @property {'pending'|'fetching'|'ready'|'skipped'|'error'} blobStatus
 * @property {number} retryCount
 * @property {number} numPages
 * @property {boolean} isLargeFile
 */

class PreloadCache {
  constructor() {
    /** @type {Map<string, CacheEntry>} */
    this.cache = new Map();
    this.totalSizeBytes = 0;
    this.activePreloads = 0;
    /** @type {Set<string>} */
    this.inFlightRequests = new Set();
    /** @type {Map<string, AbortController>} */
    this.abortControllers = new Map();
    /** @type {string|null} Currently viewed record (skip during TTL refresh) */
    this.currentlyViewedRecord = null;
  }

  /**
   * Set the currently viewed record (to skip during TTL refresh)
   * @param {string|null} fileToken
   */
  setCurrentlyViewedRecord(fileToken) {
    this.currentlyViewedRecord = fileToken;
  }

  /**
   * Get the currently viewed record
   * @returns {string|null}
   */
  getCurrentlyViewedRecord() {
    return this.currentlyViewedRecord;
  }

  /**
   * Get current cache size in bytes
   */
  getTotalSizeBytes() {
    return this.totalSizeBytes;
  }

  /**
   * Get number of cached entries
   */
  getCount() {
    return this.cache.size;
  }

  /**
   * Check if we have room for more preloads
   */
  canStartPreload(isUserClick = false) {
    if (isUserClick) return true; // User clicks bypass limit
    return this.activePreloads < PRELOAD_CONFIG.MAX_CONCURRENT_BLOBS;
  }

  /**
   * Get a cache entry by file token
   * @param {string} fileToken
   * @returns {CacheEntry|undefined}
   */
  get(fileToken) {
    const entry = this.cache.get(fileToken);
    if (entry) {
      entry.lastAccessedAt = Date.now();
    }
    return entry;
  }

  /**
   * Check if entry has a ready blob
   * @param {string} fileToken
   * @returns {boolean}
   */
  hasBlobReady(fileToken) {
    const entry = this.cache.get(fileToken);
    return entry?.blobStatus === 'ready' && entry?.blobUrl != null;
  }

  /**
   * Initialize or update a cache entry from list API data
   * @param {Object} record - Record data from list API
   */
  initializeFromListData(record) {
    const {
      file_token, signed_url, num_pages, is_large_file,
    } = record;

    if (!file_token) return;

    const existing = this.cache.get(file_token);
    const now = Date.now();

    // If we already have this entry with a valid signed_url, don't overwrite blob data
    if (existing && existing.blobStatus === 'ready') {
      // Just update the signed URL if it's newer
      if (signed_url) {
        existing.signedUrl = signed_url;
        existing.expiresAt = now + PRELOAD_CONFIG.SIGNED_URL_TTL;
      }
      return;
    }

    /** @type {CacheEntry} */
    const entry = {
      fileToken: file_token,
      signedUrl: signed_url || null,
      pdfBlob: existing?.pdfBlob || null,
      blobUrl: existing?.blobUrl || null,
      blobSizeBytes: existing?.blobSizeBytes || null,
      fetchedAt: existing?.fetchedAt || now,
      expiresAt: signed_url ? now + PRELOAD_CONFIG.SIGNED_URL_TTL : 0,
      lastAccessedAt: now,
      blobStatus: existing?.blobStatus || 'pending',
      retryCount: existing?.retryCount || 0,
      numPages: num_pages || 0,
      isLargeFile: is_large_file || false,
    };

    this.cache.set(file_token, entry);
  }

  /**
   * Mark a preload as starting
   * @param {string} fileToken
   * @returns {AbortController|null}
   */
  startPreload(fileToken) {
    const entry = this.cache.get(fileToken);
    if (!entry) return null;

    // Don't start if already fetching
    if (this.inFlightRequests.has(fileToken)) {
      return this.abortControllers.get(fileToken) || null;
    }

    entry.blobStatus = 'fetching';
    this.activePreloads += 1;
    this.inFlightRequests.add(fileToken);

    const controller = new AbortController();
    this.abortControllers.set(fileToken, controller);

    return controller;
  }

  /**
   * Complete a preload with success
   * @param {string} fileToken
   * @param {Blob} blob
   * @param {number} sizeBytes
   */
  completePreload(fileToken, blob, sizeBytes) {
    const entry = this.cache.get(fileToken);
    if (!entry) return;

    // Check size limit - discard if too large
    if (sizeBytes > PRELOAD_CONFIG.MAX_SIZE_MB_FOR_BLOB * 1024 * 1024) {
      entry.blobStatus = 'skipped';
      this.finishPreload(fileToken);
      return;
    }

    // Check if adding this would exceed total cache size
    const newTotalSize = this.totalSizeBytes + sizeBytes;
    if (newTotalSize > PRELOAD_CONFIG.MAX_CACHE_SIZE_MB * 1024 * 1024) {
      this.evictUntilSizeFits(sizeBytes);
    }

    // Check count limit - only count entries with blobs, not all cache entries
    const blobCount = Array.from(this.cache.values()).filter((e) => e.blobUrl != null).length;
    if (blobCount >= PRELOAD_CONFIG.MAX_CACHED_BLOBS) {
      this.evictOldest(1);
    }

    // Store the blob
    entry.blobUrl = URL.createObjectURL(blob);
    entry.pdfBlob = null;
    entry.blobSizeBytes = sizeBytes;
    entry.blobStatus = 'ready';
    entry.fetchedAt = Date.now();

    this.totalSizeBytes += sizeBytes;
    this.finishPreload(fileToken);
  }

  /**
   * Mark a preload as failed
   * @param {string} fileToken
   * @param {boolean} shouldRetry
   */
  failPreload(fileToken, shouldRetry = true) {
    const entry = this.cache.get(fileToken);
    if (!entry) return;

    entry.retryCount += 1;

    if (shouldRetry && entry.retryCount < 2) {
      entry.blobStatus = 'pending'; // Will be retried
    } else {
      entry.blobStatus = 'error';
    }

    this.finishPreload(fileToken);
  }

  /**
   * Cancel a preload in progress
   * @param {string} fileToken
   */
  cancelPreload(fileToken) {
    const controller = this.abortControllers.get(fileToken);
    if (controller) {
      controller.abort();
    }

    const entry = this.cache.get(fileToken);
    if (entry && entry.blobStatus === 'fetching') {
      entry.blobStatus = 'pending';
    }

    this.finishPreload(fileToken);
  }

  /**
   * Internal: Clean up after preload finishes
   * @param {string} fileToken
   */
  finishPreload(fileToken) {
    // Only decrement if this was actually an in-flight request
    if (this.inFlightRequests.has(fileToken)) {
      this.activePreloads = Math.max(0, this.activePreloads - 1);
    }
    this.inFlightRequests.delete(fileToken);
    this.abortControllers.delete(fileToken);
  }

  getEvictionCandidates() {
    const viewedRecord = this.currentlyViewedRecord;

    return Array.from(this.cache.entries())
      .filter(([fileToken, entry]) => entry.blobUrl != null && fileToken !== viewedRecord)
      .sort((a, b) => a[1].lastAccessedAt - b[1].lastAccessedAt);
  }

  /**
   * Evict entries until we have room for newSizeBytes
   * @param {number} newSizeBytes
   */
  evictUntilSizeFits(newSizeBytes) {
    const targetSize = PRELOAD_CONFIG.MAX_CACHE_SIZE_MB * 1024 * 1024 - newSizeBytes;
    const entries = this.getEvictionCandidates();

    entries.some(([fileToken]) => {
      if (this.totalSizeBytes <= targetSize) return true;
      this.evictEntry(fileToken);
      return false;
    });
  }

  /**
   * Evict the N oldest entries
   * @param {number} count
   */
  evictOldest(count) {
    const entries = this.getEvictionCandidates();

    entries.slice(0, count).forEach(([fileToken]) => {
      this.evictEntry(fileToken);
    });
  }

  /**
   * Evict a specific entry
   * @param {string} fileToken
   */
  evictEntry(fileToken) {
    const entry = this.cache.get(fileToken);
    if (!entry) return;

    // Cancel any in-flight request
    this.cancelPreload(fileToken);

    // Revoke blob URL
    if (entry.blobUrl) {
      URL.revokeObjectURL(entry.blobUrl);
      this.totalSizeBytes -= entry.blobSizeBytes || 0;
    }

    // Remove from cache
    this.cache.delete(fileToken);
  }

  /**
   * Clear entire cache
   * @param {Object} options
   * @param {boolean} options.preserveCurrentViewer - If true, keep the currently viewed record's blob URL
   */
  clear({ preserveCurrentViewer = false } = {}) {
    const viewedRecord = this.currentlyViewedRecord;

    // Cancel all in-flight requests (except currently viewed if preserving)
    Array.from(this.inFlightRequests).forEach((fileToken) => {
      if (preserveCurrentViewer && fileToken === viewedRecord) return;
      this.cancelPreload(fileToken);
    });

    // Revoke blob URLs (except currently viewed if preserving)
    this.cache.forEach((entry) => {
      if (preserveCurrentViewer && entry.fileToken === viewedRecord) return;
      if (entry.blobUrl) {
        URL.revokeObjectURL(entry.blobUrl);
      }
    });

    if (preserveCurrentViewer && viewedRecord) {
      // Preserve in-flight state for viewed record before clearing
      const wasInFlight = this.inFlightRequests.has(viewedRecord);
      const abortController = this.abortControllers.get(viewedRecord);

      // Keep only the currently viewed record
      const viewedEntry = this.cache.get(viewedRecord);
      this.cache.clear();
      if (viewedEntry) {
        this.cache.set(viewedRecord, viewedEntry);
        this.totalSizeBytes = viewedEntry.blobSizeBytes || 0;
      } else {
        this.totalSizeBytes = 0;
      }

      // Preserve in-flight tracking for viewed record
      this.inFlightRequests.clear();
      this.abortControllers.clear();

      if (wasInFlight) {
        this.activePreloads = 1;
        this.inFlightRequests.add(viewedRecord);
        if (abortController) {
          this.abortControllers.set(viewedRecord, abortController);
        }
      } else {
        this.activePreloads = 0;
      }
    } else {
      this.cache.clear();
      this.totalSizeBytes = 0;
      this.currentlyViewedRecord = null;
      this.activePreloads = 0;
      this.inFlightRequests.clear();
      this.abortControllers.clear();
    }
  }

  /**
   * Get entries that are ready for preloading
   * @returns {CacheEntry[]}
   */
  getPreloadCandidates() {
    return Array.from(this.cache.values()).filter((entry) => {
      if (entry.blobStatus !== 'pending') return false;
      if (entry.isLargeFile) return false;
      if (!entry.signedUrl) return false;
      if (Date.now() >= entry.expiresAt) return false;
      if (entry.numPages > PRELOAD_CONFIG.MAX_PAGES_FOR_BLOB) return false;
      return true;
    });
  }
}

// Singleton instance
const preloadCache = new PreloadCache();

export { preloadCache, PRELOAD_CONFIG };
export default preloadCache;
